import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Skeleton,
  InputRightElement,
  InputGroup,
  Image,
  SimpleGrid,
  useDisclosure,
  Button,
  Badge,
  Progress,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
  HStack,
  Img,
} from "@chakra-ui/react";
import DatePicker from "react-datepicker";
import calendarIcon from "../../assets/images/onleave/calendar.svg";
import IconCheck from "../../assets/images/booking/check-circle.svg";
import IconRed from "../../assets/images/booking/x-red.svg";
import { useEmployeeKPIList } from "../../hooks/useEmployeeKPIList";
import { useEmployeeAttendanceMonth } from "../../hooks/useEmployeeAttendanceMonthly";
import { useNavigate, useSearchParams } from "react-router-dom";
import { employeeCall } from "../../api/employee";
import { useCategories } from "../../categorys/employeeKPI/index";
import useDebounce from "../../hooks/useDebounce";
import { formatTableValue } from "./formatTable";
import { SearchIcon } from "@chakra-ui/icons";
import chevronLeft from "../../assets/images/employee/chevron-left.svg";
import chevronRight from "../../assets/images/employee/chevron-right.svg";
import ReactPaginate from "react-paginate";
import SearchableSelect from "../../components/selectForm";
import iconExcel from "../../assets/images/employee/iconExcel.svg";
import toast from "react-hot-toast";

const toMonthParam = (d) => {
  if (!d) return undefined;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}-${yyyy}`;
};

const fmtInt = (n) =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString("vi-VN") : "0";

const fmt1 = (n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(1) : "0.0");

const fmtPct2 = (n) =>
  Number.isFinite(Number(n)) ? `${Number(n).toFixed(2)}%` : "0.00%";

const StatCard = ({ label, value, sub, onClick, color = "#51607B" }) => (
  <Box
    bg="white"
    border="1px solid"
    borderColor="gray.200"
    borderRadius="12px"
    p="12px"
    minH="72px"
    cursor={onClick ? "pointer" : "default"}
    onClick={onClick}
    _hover={
      onClick
        ? {
            borderColor: "gray.300",
            boxShadow: "sm",
          }
        : undefined
    }
    transition="all 0.2s ease"
  >
    <Text fontSize="12px" color="#718096" fontWeight={600}>
      {label}
    </Text>
    <Text fontSize="18px" fontWeight={800} color="var(--color-info-employee)">
      {value}
    </Text>
    {sub ? (
      <Text fontSize="12px" color={color} mt="2px">
        {sub}
      </Text>
    ) : null}
  </Box>
);

const StatusData = [
  {
    key: "XUAT_SAC",
    value: "Xuất sắc",
  },
  {
    key: "DAT",
    value: "Đạt",
  },
  {
    key: "CANH_BAO",
    value: "Cảnh báo",
  },
  {
    key: "CANH_BAO_DO",
    value: "Cảnh báo đỏ",
  },
  {
    key: "CAN_HO_TRO",
    value: "Cần hỗ trợ",
  },
  {
    key: "CHUA_XEP_LOAI",
    value: "Chưa xếp loại",
  },
];

const TableEmployeeKPIList = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [searchParams] = useSearchParams();

  const [defaultValues, setDefaultValues] = useState(null);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [saleBlockId, setSaleBlockId] = useState(null);
  const [saleAreaId, setSaleAreaId] = useState(null);
  const [status, setStatus] = useState(null);
  const [monthDate, setMonthDate] = useState(new Date());
  const [zIndex, setZIndex] = useState(1);

  const debouncedSearchTerm = useDebounce(search, 500);
  const onChangeSearch = useCallback((e) => {
    const { value } = e.target;
    setSearch(value);
  });

  const {
    employeeKPIList,
    summary,
    refetch,
    isLoading,
    isRefetching,
    totalPages,
    setCurrentPage,
    currentPage,
    entries,
    startEntry,
    endEntry,
  } = useEmployeeKPIList(
    debouncedSearchTerm,
    monthDate,
    saleBlockId,
    saleAreaId,
    status,
  );

  const tableData = {
    headers: [
      {
        label: "STT",
        key: "",
      },
      {
        label: "Mã NV",
        key: "code",
      },
      {
        label: "Tên NV",
        key: "name",
      },
      {
        label: "Chi nhánh",
        key: "branch",
      },
      {
        label: "Khối",
        key: "saleBlock",
      },
      {
        label: "Vùng",
        key: "saleArea",
      },
      {
        label: "Leads",
        key: "leadCount",
      },
      {
        label: "Tư vấn",
        key: "consultCount",
      },
      {
        label: "Giao dịch",
        key: "transactionCount",
      },
      {
        label: "Đánh giá",
        key: "classification",
      },
      {
        label: "Ghi chú",
        key: "note",
      },
      {
        label: "Ngày đánh giá",
        key: "calculatedAt",
      },
    ],
    data: employeeKPIList,
  };

  const { constantData } = useCategories();

  const formatEntries = (entries) =>
    Object.entries(entries || {}).map(([key, value]) => ({ key, value }));

  const saleAreas = useMemo(
    () => formatEntries(constantData?.saleArea),
    [constantData?.saleArea],
  );

  const saleBlocks = useMemo(
    () => formatEntries(constantData?.saleBlock),
    [constantData?.saleBlock],
  );

  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage.selected + 1);
    refetch();
  };

  const navigate = useNavigate();
  const handleNavigate = (e) => {
    const options = {
      employeeId: e?.employeeId || "",
      code: e?.code || "",
      fullName: e?.name || "",
    };
    if (monthDate) {
      options.monthDate = toMonthParam(monthDate);
    }
    const searchParams = new URLSearchParams(options);

    navigate(`/employee-kpi-list/detail?${searchParams.toString()}`);
  };

  return (
    <>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" gap={{ base: "74px" }}>
          <Text fontSize={{ base: "20px" }} fontWeight={600}>
            {"Danh sách KPI nhân viên"}
          </Text>
        </Flex>

        {/* <Flex alignItems={"center"} gap={"16px"}>
          <Button
            display={"flex"}
            alignItems={"center"}
            p={"10px 16px 10px 12px"}
            gap={"8px"}
            h={"44px"}
            backgroundImage="var(--linear-gradient-excel)"
            _hover={{
              filter: "brightness(90%)",
            }}
            onClick={() => {
              toast.success("Tính năng đang phát triển");
            }}
          >
            <Image src={iconExcel} />
            <Text
              fontSize={{ base: "15px" }}
              fontWeight={600}
              color="var(--color-main)"
            >
              Xuất excel
            </Text>
          </Button>
        </Flex> */}
      </Flex>

      <Box mt={{ base: "23px" }}>
        <Flex flexDirection={"column"} gap={{ base: "16px" }}>
          <Flex
            backgroundColor="var(--color-main)"
            borderRadius={{ base: "12px" }}
            flexDirection={"column"}
          >
            <Flex
              alignItems={"center"}
              justifyContent={"space-between"}
              gap={{ base: "32px" }}
              p={"16px 16px 12px 16px"}
            >
              <Flex
                alignItems={"flex-start"}
                gap={{ base: "16px 32px" }}
                flex={"1"}
                flexDirection={"column"}
              >
                <InputGroup
                  maxW="42vw"
                  minW={"42vw"}
                  borderRadius={{ base: "8px" }}
                  w={{ base: "100%" }}
                >
                  <Input
                    placeholder="Nhập mã NV ... "
                    type="text"
                    borderRadius={{ base: "8px" }}
                    border={{ base: "1px solid var(--color-boder)" }}
                    backgroundColor={"var(--fill-avatar)"}
                    onChange={onChangeSearch}
                  />
                  <InputRightElement
                    borderTopRightRadius={"8px"}
                    borderBottomRightRadius={"8px"}
                    pointerEvents="none"
                    cursor={"pointer"}
                    backgroundImage="var(--color-button)"
                  >
                    <SearchIcon color="var(--color-main)" />
                  </InputRightElement>
                </InputGroup>

                <Flex
                  alignItems={"center"}
                  gap={{ base: "16px 32px" }}
                  flex={"1"}
                  flexWrap={"wrap"}
                  w={"100%"}
                >
                  <InputGroup w={"100%"} maxW={"255px"} zIndex={zIndex}>
                    <DatePicker
                      selected={monthDate}
                      onChange={(d) => setMonthDate(d)}
                      showMonthYearPicker
                      dateFormat="MM/yyyy"
                      locale="vi"
                      isClearable
                      placeholderText="Chọn tháng"
                      className="datePickerStyle"
                      onFocus={() => setZIndex(3)}
                      onBlur={() => setZIndex(1)}
                    />
                    {!monthDate && (
                      <InputRightElement pointerEvents="none" h={"44px"}>
                        <Image src={calendarIcon} />
                      </InputRightElement>
                    )}
                  </InputGroup>

                  <SearchableSelect
                    isShowSearch={false}
                    options={saleBlocks}
                    initialSelectedOption={{ key: 0, value: "Chọn khối" }}
                    setValue={setSaleBlockId}
                  />
                  <SearchableSelect
                    isShowSearch={false}
                    options={saleAreas}
                    initialSelectedOption={{ key: 0, value: "Chọn vùng" }}
                    setValue={setSaleAreaId}
                  />
                  <SearchableSelect
                    isShowSearch={true}
                    options={StatusData}
                    initialSelectedOption={{ key: 0, value: "Chọn trạng thái" }}
                    setValue={setStatus}
                  />
                </Flex>
              </Flex>
            </Flex>

            <Box width={"100%"} height={"100%"}>
              <Skeleton isLoaded={!isLoading && !isRefetching}>
                <Box width={"100%"} height={"100%"}>
                  <Skeleton isLoaded={!isLoading && !isRefetching}>
                    <Box padding={4}>
                      <SimpleGrid
                        columns={{ base: 2, md: 3, lg: 4 }}
                        spacing="10px"
                        w={"100%"}
                      >
                        <StatCard
                          label="Xuất sắc"
                          value={fmtInt(
                            summary?.classifications?.XUAT_SAC || 0,
                          )}
                        />
                        <StatCard
                          label="Đạt"
                          value={fmtInt(summary?.classifications?.DAT || 0)}
                        />
                        <StatCard
                          label="Cảnh báo"
                          value={fmtInt(
                            summary?.classifications?.CANH_BAO || 0,
                          )}
                        />
                        <StatCard
                          label="Cần hỗ trợ"
                          value={fmtInt(
                            summary?.classifications?.CAN_HO_TRO || 0,
                          )}
                        />
                      </SimpleGrid>
                    </Box>
                  </Skeleton>
                </Box>

                <TableContainer
                  w={"100%"}
                  maxH="calc(100vh - 350.5px)"
                  overflowY="auto"
                >
                  <Table w={"100%"} variant="unstyled">
                    <Thead
                      position="sticky"
                      top="0"
                      zIndex="1"
                      w={"100%"}
                      h={"41px"}
                      color="white"
                      fontWeight="400"
                      backgroundColor="#F9FAFD"
                    >
                      <Tr h={{ base: "41px" }}>
                        {tableData.headers.map((e, index) => {
                          let width = "auto";
                          let textAlign = "auto";
                          if (e.key === "") {
                            width = "2%";
                            textAlign = "center";
                          } else if (e.key === "code") {
                            width = "5%";
                            textAlign = "start";
                          } else if (e.key === "name") {
                            width = "10%";
                            textAlign = "start";
                          } else if (e.key === "saleBlock") {
                            width = "20%";
                            textAlign = "start";
                          } else if (e.key === "saleArea") {
                            width = "10%";
                            textAlign = "start";
                          } else if (e.key === "branch") {
                            width = "10%";
                            textAlign = "start";
                          } else if (e.key === "consultCount") {
                            width = "10%";
                            textAlign = "start";
                          } else if (e.key === "leadCount") {
                            width = "10%";
                            textAlign = "start";
                          } else if (e.key === "transactionCount") {
                            width = "10%";
                            textAlign = "start";
                          } else if (e.key === "classification") {
                            width = "10%";
                            textAlign = "start";
                          } else if (e.key === "note") {
                            width = "20%";
                            textAlign = "start";
                          } else {
                            width = "10%";
                            textAlign = "end";
                          }
                          return (
                            <Td
                              p={"16px 16px 8px 16px"}
                              key={index}
                              border={"none"}
                              color={"#51607B"}
                              fontSize={{ base: "14px" }}
                              w={width}
                            >
                              <Box textAlign={"center"} fontWeight={"bold"}>{e.label}</Box>
                            </Td>
                          );
                        })}
                      </Tr>
                    </Thead>
                    <Tbody w={"100%"} h={"100%"}>
                      {tableData.data?.length > 0 ? (
                        tableData.data?.map((e, rowIndex) => {
                          const items = { ...e };
                          delete items.id;
                          delete items.employeeId;
                          const keyValues = Object.keys(items);
                          return (
                            <Tr key={rowIndex} h={"72px"}>
                              <Td
                                p={"16px 16px 8px 16px"}
                                w={"2%"}
                                bg={
                                  rowIndex % 2 === 0
                                    ? "var(--color-main)"
                                    : "var(--fill-avatar)"
                                }
                              >
                                {rowIndex + 1}
                              </Td>
                              {keyValues.map((keyvalue, index) => {
                                let width = "auto";
                                let widthText = "100%";
                                let textAlign = "auto";
                                let whiteSpace = "inherit";
                                if (keyvalue === "") {
                                  width = "2%";
                                  textAlign = "center";
                                } else if (keyvalue === "code") {
                                  width = "5%";
                                  textAlign = "start";
                                } else if (keyvalue === "name") {
                                  width = "10%";
                                  textAlign = "start";
                                } else if (keyvalue === "saleBlock") {
                                  width = "20%";
                                  textAlign = "start";
                                } else if (keyvalue === "saleArea") {
                                  width = "10%";
                                  textAlign = "start";
                                } else if (keyvalue === "branch") {
                                  width = "10%";
                                  textAlign = "start";
                                } else if (keyvalue === "consultCount") {
                                  width = "10%";
                                  textAlign = "start";
                                } else if (keyvalue === "leadCount") {
                                  width = "10%";
                                  textAlign = "start";
                                } else if (keyvalue === "transactionCount") {
                                  width = "10%";
                                  textAlign = "start";
                                } else if (keyvalue === "classification") {
                                  width = "10%";
                                  textAlign = "start";
                                } else if (keyvalue === "note") {
                                  width = "10%";
                                  textAlign = "start";
                                } else {
                                  width = "10%";
                                  textAlign = "end";
                                  whiteSpace = "inherit";
                                }
                                return (
                                  <Td
                                    p={"16px 16px 8px 16px"}
                                    w={width}
                                    key={index}
                                    bg={
                                      rowIndex % 2 === 0
                                        ? "var(--color-main)"
                                        : "var(--fill-avatar)"
                                    }
                                  >
                                    <Box
                                      fontSize={{ base: "14px" }}
                                      lineHeight={"19.6px"}
                                      textAlign={"start"}
                                      fontWeight={500}
                                      color={"var(--color-info-employee)"}
                                      w={widthText}
                                      whiteSpace={whiteSpace}
                                      overflow="hidden"
                                      textOverflow="ellipsis"
                                      display="-webkit-box"
                                      sx={{
                                        WebkitLineClamp: "2",
                                        WebkitBoxOrient: "vertical",
                                      }}
                                    >
                                      {keyvalue === "name"
                                        ? formatTableValue(
                                            e[keyvalue],
                                            keyvalue,
                                            () => handleNavigate(e),
                                          )
                                        : formatTableValue(
                                            e[keyvalue],
                                            keyvalue,
                                          )}
                                    </Box>
                                  </Td>
                                );
                              })}
                            </Tr>
                          );
                        })
                      ) : (
                        <Tr>
                          <Td
                            colSpan={tableData.headers.length}
                            textAlign="center"
                            padding={"40px 0"}
                          >
                            Không có dữ liệu
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Skeleton>
              <Box mt={10} mr={5} mb={5} ml={5}>
                <Flex alignItems={"center"} justifyContent={"space-between"}>
                  <Text>
                    Showing {startEntry} to {endEntry} of {entries} entries
                  </Text>
                  <HStack spacing={2} justify="flex-end">
                    <ReactPaginate
                      className="pagination-custom"
                      pageCount={totalPages}
                      pageRangeDisplayed={3}
                      marginPagesDisplayed={1}
                      onPageChange={handlePageChange}
                      containerClassName={"pagination"}
                      activeClassName={"active"}
                      breakClassName={"ellipsis"}
                      breakLabel={"..."}
                      previousLabel={
                        <Img
                          w={"16px"}
                          minW={"16px"}
                          h={"16px"}
                          src={chevronLeft}
                        />
                      }
                      nextLabel={
                        <Img
                          w={"16px"}
                          minW={"16px"}
                          h={"16px"}
                          src={chevronRight}
                        />
                      }
                      renderOnZeroPageCount={null}
                      forcePage={currentPage - 1}
                    />
                  </HStack>
                </Flex>
              </Box>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </>
  );
};

export default TableEmployeeKPIList;
